import { formatToAddressSystem } from '@/utils/formatToFullVisibleAddress'

import {
  STATE_CONTENT_ADDRESS_SYSTEM,
  STATE_DIALOG_ADDRESS_SYSTEM,
} from '@/constants/statesOfAddressSystem'

import { useAddressesStore } from '@/store/addresses/addresses'
import {
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { useAppStore } from '@/store/app/app'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketPointStore } from '@/store/basket/basket-point'
import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { useUserStore } from '@/store/user/user'

import {
  DeliveryMethods,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import { Success } from '@/api/results'

import type { PointProperties } from '@/api/modules/delivery/types'
import { storage } from '@/services/storage'
import { STORAGE_KEY } from '@/services/storage/constants'

type Emit = (event: 'update:modelValue', toggle: boolean) => void

export const useAddressSystemDialog = (emit: Emit) => {
  const { $metrics } = useNuxtApp()

  const addressSystemState = useAddressSystemStateStore()
  const addressesStore = useAddressesStore()
  const pointStore = useBasketPointStore()
  const basketStore = useBasketStore()
  const userStore = useUserStore()

  const { showSnackbar } = useAppStore()

  const {
    changeDeliveryBasket,
    setLoadingWidget,
    changeDeliveryWidget,
    resetDeliveryAddress,
    setDeliveryAddress,
  } = useAddressSystemStore()

  const { changeDialogState, changeDialogContent } =
    useAddressSystemStateStore()

  const { deliveryAddress, deliveryWidget, deliveryBasket } = storeToRefs(
    useAddressSystemStore(),
  )

  const { prevDialogContent } = storeToRefs(useAddressSystemStateStore())

  const isAuthenticated = computed(() => userStore.isAuthenticated)

  const deliveryMethod = computed(() => basketStore.data.delivery.method)

  const deliveryBasketId = computed(() => {
    return deliveryBasket.value.delivery_method === DeliveryMethods.COURIER
      ? deliveryBasket.value.address_id
      : deliveryBasket.value.point_id
  })

  const stateDialogContent = computed(() => addressSystemState.dialogContent)

  const addresses = computed(() => addressesStore.addresses.data)

  const onChangeDialogModelValue = (toggle: boolean = false) => {
    emit('update:modelValue', toggle)
  }

  // Установка адреса в корзину
  const onChangeBasketAddress = async () => {
    // В случае если адрес является другого города
    if (deliveryBasket.value.allow_redirect) {
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.OTHER_CITY)

      return
    }

    $metrics?.eventSendGoal({ ym: 'address_save_widget' })

    if (deliveryBasket.value.delivery_method === DeliveryMethods.COURIER) {
      await changeCourierBasketAddress()
    } else {
      await changePointsBasketAddress()
    }

    // В корзине сортируем список адресов - выбранный адрес ставим первым
    const basketPointsSorted = [...pointStore.data].sort((point) =>
      point.id === deliveryBasket.value.point_id ? -1 : 0,
    )
    pointStore.setPoints({ data: basketPointsSorted })
  }

  const isBasketAddressAlreadySet = () => {
    if (deliveryMethod.value === deliveryBasket.value.delivery_method) {
      // Установленный id адрес в корзине
      const basketAddressId =
        basketStore.data.delivery.addresses[deliveryMethod.value]

      const { address_id, point_id } = deliveryBasket.value

      // Если установленный адрес совпадает с выбранным из списка
      return basketAddressId === address_id || basketAddressId === point_id
    }

    return false
  }

  // Установка адреса для доставки
  const changeCourierBasketAddress = async () => {
    await createCourierNewAddress()

    if (deliveryBasket.value.allow_redirect) {
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.REDIRECT)

      setLoadingWidget(false)
      return
    }

    // Если пользователь авторизован и адрес уже установлен, то закрываем ДО
    if (isAuthenticated.value && isBasketAddressAlreadySet()) {
      return onChangeDialogModelValue()
    }

    if (!isAuthenticated.value) {
      const { address } = deliveryAddress.value?.address || {}

      const city = address?.city || ''
      const street = address?.street_with_type || ''
      const house = address?.house || ''

      const formattedAddress = formatToAddressSystem(city, street, house)

      const payload: SetDeliveryMethodPayload = {
        order_address: formattedAddress,
        delivery_method: DeliveryMethods.COURIER,
      }

      changeDeliveryWidget(payload)
      changeDeliveryBasket(payload)

      // Пушим в local store данные о адресе
      storage.set(
        STORAGE_KEY.addressSystem.deliveryAddress,
        deliveryAddress.value,
      )
    }

    await setBasketDelivery()

    setLoadingWidget(false)

    onChangeDialogModelValue()
  }

  // Установка адреса для самовывоза/в ресторане
  const changePointsBasketAddress = async () => {
    // Если пользователь авторизован и адрес уже установлен, то закрываем ДО
    if (isAuthenticated.value && isBasketAddressAlreadySet()) {
      return onChangeDialogModelValue()
    }

    changeDeliveryWidget(deliveryBasket.value)
    changeDeliveryBasket(deliveryBasket.value)

    await setBasketDelivery()

    setLoadingWidget(false)

    onChangeDialogModelValue()
  }

  // Создание нового адреса для доставки
  const createCourierNewAddress = async () => {
    const isAvailableToCreate =
      isAuthenticated.value &&
      stateDialogContent.value === STATE_CONTENT_ADDRESS_SYSTEM.NEW

    if (!isAvailableToCreate) return

    setLoadingWidget(true)

    const { $apiService } = useNuxtApp()

    const [longitude, latitude] = deliveryAddress.value.coordinates

    const payload = {
      coords: { latitude, longitude },
      data: {
        selectedSuggestion: deliveryAddress.value?.address?.address || null,
        house: deliveryAddress.value?.address?.address?.house || '',
        notes: '',
        porch: '',
        floor: '',
        intercom: '',
        flat: '',
      },
    }

    const address = await $apiService.addresses.createNewAddress(
      payload.coords,
      payload.data,
    )

    if (!(address instanceof Success)) {
      showSnackbar({ message: address.message })

      $metrics?.eventSendGoal({ ym: 'address_save_error' })

      return
    }

    await addressesStore.getAddresses()

    setLoadingWidget(false)

    const delivery: SetDeliveryMethodPayload = {
      delivery_method: DeliveryMethods.COURIER,
      address_id: address.data.id,
      allow_redirect: address.data.allow_redirect,
      city_domain_prefix: address.data.city_domain_prefix,
    }

    changeDeliveryWidget(delivery)
    changeDeliveryBasket(delivery)
  }

  // Метод определения способа доставки и установленного адреса
  const initBasketDelivery = () => {
    if (!isAuthenticated.value) {
      changeDeliveryBasket({ ...deliveryWidget.value })

      return
    }

    const { addresses, method } = basketStore.data.delivery

    // Определяем property через способ доставки
    const detectedProperty =
      method === DeliveryMethods.COURIER ? 'address_id' : 'point_id'

    const delivery: SetDeliveryMethodPayload = {
      delivery_method: method,
      [detectedProperty]: addresses[method],
      order_address: deliveryBasket.value.order_address,
    }

    changeDeliveryBasket(delivery)
  }

  const setBasketDelivery = async () => {
    if (!isAuthenticated.value) return

    await basketStore.setDeliveryMethod(deliveryBasket.value)
  }

  const setInitStateOfDialog = async () => {
    // Если клиент не авторизован
    if (!isAuthenticated.value) {
      changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.READY)
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.NEW)

      $metrics?.eventSendGoal({ ym: 'map_window_open' })
      return
    }

    // В случае если список адресов клиента уже есть
    if (addresses.value.length) {
      changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.READY)
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.SELECT)

      $metrics?.eventSendGoal({ ym: 'basket_saved_address_window_open' })
      return
    }

    // Запрашиваем адреса клиента если нет списка
    const result = await addressesStore.getAddresses()

    // Если ошибка при получении адресов
    if (!(result instanceof Success)) {
      changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.READY)
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.NEW)

      $metrics?.eventSendGoal({ ym: 'map_window_open' })
      return
    }

    // В случае если пришел пустой список адресов
    if (!addresses.value.length) {
      changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.READY)
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.NEW)

      $metrics?.eventSendGoal({ ym: 'map_window_open' })
      return
    }

    // Если список адресов пришел не пустым
    changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.READY)
    changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.SELECT)

    $metrics?.eventSendGoal({ ym: 'basket_saved_address_window_open' })
  }

  const points = computed(() => pointStore.data)

  const fetchPoint = async () => {
    // Если нет точек, то запрашиваем
    if (!points.value.length) await pointStore.getPoints()
  }

  const resetDialogState = () => {
    // Определяем начальное состояние контента в ДО
    const initState = isAuthenticated.value
      ? STATE_CONTENT_ADDRESS_SYSTEM.SELECT
      : STATE_CONTENT_ADDRESS_SYSTEM.NEW

    changeDialogContent(initState)
    changeDialogState(STATE_DIALOG_ADDRESS_SYSTEM.LOADING)

    if (isAuthenticated.value) resetDeliveryAddress()
  }

  // Определяем начальное состояние контента в ДО
  resetDialogState()

  const dialogState = computed(() => addressSystemState.dialogState)

  // Опредляем отображение контента АД
  const isVisibleContent = computed(() => {
    const statesDialog = [
      STATE_DIALOG_ADDRESS_SYSTEM.READY,
      STATE_DIALOG_ADDRESS_SYSTEM.LOADING,
    ]

    const statesOfContent = [
      STATE_CONTENT_ADDRESS_SYSTEM.NEW,
      STATE_CONTENT_ADDRESS_SYSTEM.SELECT,
    ]

    return (
      statesDialog.includes(dialogState.value) &&
      statesOfContent.includes(stateDialogContent.value)
    )
  })

  const setSelectedRestaurant = (point: PointProperties | null) => {
    const payload: SetDeliveryMethodPayload = {
      delivery_method: deliveryBasket.value.delivery_method,
      point_id: point?.id,
      order_address: point?.address,
    }

    // Если ресторан другого города - показываем модалку с редиректом на другой город
    if (point?.allow_redirect) {
      payload.city_domain_prefix = point?.city_domain_prefix
      changeDialogContent(STATE_CONTENT_ADDRESS_SYSTEM.REDIRECT)
    }

    changeDeliveryBasket(payload)
  }

  const initAddressStorageInDeliveryAddress = () => {
    const initDeliveryAddress: DeliveryDataAddress | null = storage.get(
      STORAGE_KEY.addressSystem.deliveryAddress,
    )

    if (initDeliveryAddress) {
      setDeliveryAddress(initDeliveryAddress)
      $metrics?.eventSendGoal({ ym: 'address_save_widget' })
    }
  }

  const refuseChangeOnOtherCity = () => {
    changeDeliveryBasket({
      delivery_method: deliveryBasket.value.delivery_method,
    })

    changeDialogContent(prevDialogContent.value)
  }

  return {
    stateDialogContent,
    deliveryBasket,
    deliveryBasketId,
    prevDialogContent,
    onChangeDialogModelValue,
    onChangeBasketAddress,
    resetDialogState,
    dialogState,
    isVisibleContent,
    fetchPoint,
    initBasketDelivery,
    setInitStateOfDialog,
    setSelectedRestaurant,
    initAddressStorageInDeliveryAddress,
    refuseChangeOnOtherCity,
  }
}
